import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Fade,
  Backdrop,
  useTheme,
} from '@mui/material';
import {
  Close,
  ArrowBack,
  ArrowForward,
  CheckCircle,
} from '@mui/icons-material';

export interface TourStep {
  target?: string; // CSS selector for the target element
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

interface TourProps {
  steps: TourStep[];
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

export const Tour: React.FC<TourProps> = ({ steps, open, onClose, onComplete }) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  const step = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const progress = ((currentStep + 1) / steps.length) * 100;

  const calculatePosition = useCallback(() => {
    if (!step?.target) {
      // Center position for intro steps
      setTargetRect(null);
      setTooltipPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
      });
      return;
    }

    const element = document.querySelector(step.target);
    if (!element) {
      setTargetRect(null);
      setTooltipPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
      });
      return;
    }

    const rect = element.getBoundingClientRect();
    setTargetRect(rect);

    const placement = step.placement || 'bottom';
    const tooltipWidth = 380;
    const tooltipHeight = 200;
    const offset = 16;

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'top':
        top = rect.top - tooltipHeight - offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - offset;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + offset;
        break;
      default:
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    // Keep tooltip within viewport
    const padding = 20;
    if (left < padding) left = padding;
    if (left + tooltipWidth > window.innerWidth - padding) {
      left = window.innerWidth - tooltipWidth - padding;
    }
    if (top < padding) top = rect.bottom + offset;
    if (top + tooltipHeight > window.innerHeight - padding) {
      top = rect.top - tooltipHeight - offset;
    }

    setTooltipPosition({ top, left });
  }, [step]);

  useEffect(() => {
    if (open) {
      calculatePosition();
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition);
      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition);
      };
    }
  }, [open, currentStep, calculatePosition]);

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
    }
  }, [open]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete?.();
      onClose();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop with spotlight effect */}
      <Backdrop
        open={open}
        sx={{
          zIndex: 99998,
              backdropFilter: 'blur(.5px)',
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
        }}
      />

      {/* Spotlight on target element */}
      {targetRect && (
        <Box
          sx={{
            position: 'fixed',
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            borderRadius: 2,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)',
            zIndex: 99999,
            pointerEvents: 'none',
            transition: 'all 0.3s ease-in-out',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -4,
              left: -4,
              right: -4,
              bottom: -4,
              borderRadius: 3,
              border: `3px solid ${theme.palette.primary.main}`,
              animation: 'pulse 2s infinite',
            },
            '@keyframes pulse': {
              '0%': {
                boxShadow: `0 0 0 0 ${theme.palette.primary.main}40`,
              },
              '70%': {
                boxShadow: `0 0 0 10px ${theme.palette.primary.main}00`,
              },
              '100%': {
                boxShadow: `0 0 0 0 ${theme.palette.primary.main}00`,
              },
            },
          }}
        />
      )}

      {/* Tooltip */}
      <Fade in={open}>
        <Paper
          elevation={24}
          sx={{
            position: 'fixed',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            transform: !step?.target ? 'translate(-50%, -50%)' : 'none',
            width: 380,
            maxWidth: 'calc(100vw - 40px)',
            zIndex: 100000,
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'top 0.3s ease-in-out, left 0.3s ease-in-out',
          }}
        >
          {/* Header with gradient */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              p: 2.5,
              position: 'relative',
            }}
          >
            <IconButton
              onClick={handleSkip}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                opacity: 0.8,
                '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <Close fontSize="small"  sx={{
                   color:'white !important'
              }}/>
            </IconButton>

            <Typography
              variant="overline"
              sx={{
                opacity: 0.9,
                letterSpacing: 1.5,
                fontSize: '0.7rem',
                  color:'white !important'
              }}
            >
              Step {currentStep + 1} of {steps.length}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mt: 0.5,
                pr: 3,
                    color:'white !important'
              }}
            >
              {step?.title}
            </Typography>
          </Box>

          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              bgcolor: theme.palette.grey[200],
              '& .MuiLinearProgress-bar': {
                bgcolor: theme.palette.primary.main,
              },
            }}
          />

          {/* Content */}
          <Box sx={{ p: 2.5 }}>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.7,
                fontSize: '0.95rem',
              }}
            >
              {step?.content}
            </Typography>
          </Box>

          {/* Step indicators */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 0.75,
              pb: 2,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: index === currentStep ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor:
                    index === currentStep
                      ? theme.palette.primary.main
                      : index < currentStep
                      ? theme.palette.primary.light
                      : theme.palette.grey[300],
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Box>

          {/* Footer with buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2.5,
              py: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.grey[50],
            }}
          >
            <Button
              onClick={handleSkip}
              sx={{
                color: theme.palette.text.secondary,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'transparent',
                  color: theme.palette.text.primary,
                },
              }}
            >
              Skip Tour
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isFirstStep && (
                <Button
                  variant="outlined"
                  onClick={handlePrev}
                  startIcon={<ArrowBack />}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2,
                  }}
                >
                  Back
                </Button>
              )}

              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={isLastStep ? <CheckCircle /> : <ArrowForward />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${theme.palette.primary.main}40`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                  },
                }}
              >
                {isLastStep ? 'Finish' : 'Next'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default Tour;
